/**
 * AddPhotoMarkerCommand - Command for adding photo markers with undo/redo support.
 *
 * Extends BaseAddEntityCommand with photo marker-specific behavior:
 * - Tracks the marker number for undo/redo events
 * - Notifies the PhotoMarkerManager of changes
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { PhotoMarkerManager } from "../managers/PhotoMarkerManager";
import { BaseAddEntityCommand } from "./BaseAddEntityCommand";

export interface AddPhotoMarkerParams {
  /** Handles of all entities to track: block reference + OdDbAttribute instances */
  entityHandles: string[];
  /** The original layer name to restore on redo */
  originalLayerName: string;
  /** The marker number for this photo marker */
  markerNumber: number;
}

/**
 * Command for adding a photo marker with soft-delete undo.
 * Undo moves all tracked entities (block ref + attributes) to hidden layer.
 */
export class AddPhotoMarkerCommand extends BaseAddEntityCommand {
  private markerNumber: number;
  private photoMarkerManager: PhotoMarkerManager | null;

  constructor(
    viewer: DRDrawingViewer,
    params: AddPhotoMarkerParams,
    photoMarkerManager?: PhotoMarkerManager,
  ) {
    super(viewer, {
      entityHandles: params.entityHandles,
      originalLayerName: params.originalLayerName,
    });
    this.markerNumber = params.markerNumber;
    this.photoMarkerManager = photoMarkerManager ?? null;
  }

  /**
   * Only the head entity (block reference in block mode, the single text entity
   * in entity/text_only mode — always entityHandles[0], see creation.ts) represents
   * "the" marker. Its attribute(s) are components, not separate DR entities: they
   * must never get their own DR_META/oplog CREATED event, or they show up as an
   * unexplained "geometry" row in the Activity feed (getEntityType doesn't
   * recognize OdDbAttribute, it isn't the exact class OdDbText). Undo/redo still
   * uses the full `handles` list — the whole marker must hide/reappear as a unit.
   */
  protected override get metaHandles(): string[] {
    return this.handles.slice(0, 1);
  }

  /**
   * Undo - move to hidden layer and notify manager.
   */
  override undo(): void {
    super.undo();
    this.photoMarkerManager?.notifyChange();
  }

  /**
   * Redo - restore to original layer and notify manager.
   */
  override redo(): void {
    super.redo();
    this.photoMarkerManager?.notifyChange();
  }
}
